import { useRef, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { IconMenu2, IconUserCircle } from '@tabler/icons-react';

import { Button } from '@components/common/Button';
import { Container } from '@components/common/Container';
import { ThemeToggle } from '@components/common/ThemeToggle';
import { Logo } from '@components/Logo';
import { ABOUT_SECTION_IDS, PUBLIC_NAV_LINKS, normalizePublicPath } from '@constants/publicSearch';
import { withPublicSiteView } from '@constants/publicSiteView';
import { useAuth } from '@contexts/AuthContext';
import { useRegistrationVisibility } from '@hooks/useRegistrationVisibility';

/** True when tablet portrait: show profile icon; false = desktop/tablet landscape = show Log in / Register buttons */
function useIsTabletPortrait(): boolean {
  const [isTabletPortrait, setIsTabletPortrait] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia(
      '(min-width: 640px) and (max-width: 1359px) and (orientation: portrait)'
    );
    const handler = () => setIsTabletPortrait(mql.matches);
    handler();
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return isTabletPortrait;
}

export interface LandingHeaderProps {
  isScrolled: boolean;
  isMobile: boolean;
  isMobileMenuOpen: boolean;
  onOpenMobileMenu: () => void;
  /** Section id (without #) for scroll-spy active state, e.g. "about", "how-to-join" */
  activeSectionId?: string | null;
  /** Signed-in member intentionally previewing the public site (`/?view=public`). */
  viewingPublicSite?: boolean;
}

export function LandingHeader({
  isScrolled,
  isMobile,
  isMobileMenuOpen,
  onOpenMobileMenu,
  activeSectionId = null,
  viewingPublicSite = false,
}: LandingHeaderProps) {
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const isTabletPortrait = useIsTabletPortrait();
  const { showRegister } = useRegistrationVisibility();
  const { user, token, logout, isLoggingOut } = useAuth();
  const isLoggedIn = Boolean(user && token);

  const handleLogoClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (normalizePublicPath(pathname) !== '/') return;
    event.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (!accountMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target as Node)) {
        setAccountMenuOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAccountMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [accountMenuOpen]);

  // Signed-in header buttons: Dashboard + Log out when previewing the public site, else Log out.
  const signedInActionButtons = viewingPublicSite ? (
    <>
      <Link
        to="/dashboard"
        className={`bhg-button bhg-button--primary bhg-button--${isScrolled ? 'sm' : 'md'}`}
      >
        Dashboard
      </Link>
      <Button
        type="button"
        variant="ghost"
        size={isScrolled ? 'sm' : 'md'}
        onClick={logout}
        loading={isLoggingOut}
        aria-busy={isLoggingOut}
      >
        {isLoggingOut ? 'Signing out…' : 'Log out'}
      </Button>
    </>
  ) : (
    <Button
      type="button"
      variant="ghost"
      size={isScrolled ? 'sm' : 'md'}
      onClick={logout}
      loading={isLoggingOut}
      aria-busy={isLoggingOut}
    >
      {isLoggingOut ? 'Signing out…' : 'Log out'}
    </Button>
  );

  // Signed-in account dropdown items (tablet portrait).
  const signedInDropdownItems = viewingPublicSite ? (
    <>
      <Link
        to="/dashboard"
        className="bhg-landing-header__account-dropdown-item"
        role="menuitem"
        onClick={() => setAccountMenuOpen(false)}
      >
        Dashboard
      </Link>
      <Button
        type="button"
        variant="ghost"
        size="md"
        className="bhg-landing-header__account-dropdown-item"
        role="menuitem"
        loading={isLoggingOut}
        aria-busy={isLoggingOut}
        onClick={() => logout()}
      >
        {isLoggingOut ? 'Signing out…' : 'Log out'}
      </Button>
    </>
  ) : (
    <Button
      type="button"
      variant="ghost"
      size="md"
      className="bhg-landing-header__account-dropdown-item"
      role="menuitem"
      loading={isLoggingOut}
      aria-busy={isLoggingOut}
      onClick={() => logout()}
    >
      {isLoggingOut ? 'Signing out…' : 'Log out'}
    </Button>
  );

  return (
    <header
      className={`bhg-landing-header ${isScrolled ? 'bhg-landing-header--scrolled' : ''}`}
    >
      <Container size="lg">
        <div className="bhg-landing-header__content">
          <Link
            to={withPublicSiteView('/', viewingPublicSite)}
            className="bhg-landing-header__logo-link"
            onClick={handleLogoClick}
            aria-label="Go to top of page"
          >
            <Logo
              variant="primaryBlue"
              size={isScrolled ? 'small' : 'medium'}
              showText={!isScrolled}
            />
          </Link>
          <div className="bhg-landing-header__right">
            <nav className="bhg-landing-nav" role="navigation" aria-label="Main navigation">
              {PUBLIC_NAV_LINKS.map(({ href, label, ariaLabel, subLinks, sectionId }) => {
                const isAbout = subLinks !== undefined;
                const isActive = isAbout
                  ? activeSectionId != null && ABOUT_SECTION_IDS.includes(activeSectionId)
                  : activeSectionId === sectionId;
                const to = withPublicSiteView(href, viewingPublicSite);
                if (subLinks) {
                  return (
                    <div key={href} className="bhg-landing-nav__dropdown">
                      <Link
                        to={to}
                        className={`bhg-landing-nav__link${isActive ? ' bhg-landing-nav__link--active' : ''}`}
                        aria-label={ariaLabel}
                        aria-haspopup="true"
                        aria-expanded="false"
                      >
                        {label}
                      </Link>
                      <div className="bhg-landing-nav__dropdown-panel" role="menu">
                        {subLinks.map((sub) => {
                          const subActive = activeSectionId === sub.sectionId;
                          return (
                            <Link
                              key={sub.href}
                              to={withPublicSiteView(sub.href, viewingPublicSite)}
                              role="menuitem"
                              className={`bhg-landing-nav__dropdown-link${subActive ? ' bhg-landing-nav__dropdown-link--active' : ''}`}
                            >
                              {sub.label}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                return (
                  <Link
                    key={href}
                    to={to}
                    className={`bhg-landing-nav__link${isActive ? ' bhg-landing-nav__link--active' : ''}`}
                    aria-label={ariaLabel}
                    aria-current={isActive ? 'true' : undefined}
                  >
                    {label}
                  </Link>
                );
              })}
            </nav>
            <div className="bhg-landing-header__actions">
              {/* Desktop + tablet landscape: Log in + Register, or Log out when logged in (has token) */}
              <div
                className="bhg-landing-header__actions-buttons"
                aria-hidden={isTabletPortrait ? true : undefined}
              >
                {isLoggedIn ? signedInActionButtons : (
                  <>
                    <Link
                      to="/login"
                      className={`bhg-button bhg-button--ghost bhg-button--${isScrolled ? 'sm' : 'md'}`}
                    >
                      Log in
                    </Link>
                    {showRegister ? (
                      <Link
                        to="/register"
                        className={`bhg-button bhg-button--secondary bhg-button--${isScrolled ? 'sm' : 'md'}`}
                      >
                        Register
                      </Link>
                    ) : null}
                  </>
                )}
              </div>
              {/* Tablet portrait only: icon + dropdown */}
              <div
                className="bhg-landing-header__actions-account"
                ref={accountMenuRef}
                aria-hidden={isTabletPortrait ? undefined : true}
              >
                <button
                  type="button"
                  className="bhg-landing-header__account-trigger"
                  onClick={() => setAccountMenuOpen((prev) => !prev)}
                  aria-expanded={accountMenuOpen}
                  aria-haspopup="true"
                  aria-label={isLoggedIn ? 'Account' : 'Log in or Register'}
                >
                  <IconUserCircle
                    size={isScrolled ? 24 : 28}
                    stroke={1.25}
                  />
                </button>
                <div
                  className="bhg-landing-header__account-dropdown"
                  role="menu"
                  aria-label="Account actions"
                  hidden={!accountMenuOpen}
                >
                  {isLoggedIn ? signedInDropdownItems : (
                    <>
                      <Link
                        to="/login"
                        className="bhg-landing-header__account-dropdown-item"
                        role="menuitem"
                        onClick={() => setAccountMenuOpen(false)}
                      >
                        Log in
                      </Link>
                      {showRegister ? (
                        <Link
                          to="/register"
                          className="bhg-landing-header__account-dropdown-item"
                          role="menuitem"
                          onClick={() => setAccountMenuOpen(false)}
                        >
                          Register
                        </Link>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="bhg-landing-mobile-controls">
            <button
              type="button"
              className="bhg-mobile-menu-button"
              onClick={onOpenMobileMenu}
              aria-label="Open menu"
              aria-controls="bhg-mobile-menu"
              aria-expanded={isMobileMenuOpen}
            >
              <IconMenu2 size={22} />
            </button>
          </div>
        </div>
      </Container>
      {/* Theme toggle: far right (desktop); hidden on mobile – use ThemeToggle from context */}
      {!isMobile && (
        <div className="bhg-landing-header__theme-toggle">
          <ThemeToggle />
        </div>
      )}
    </header>
  );
}
